/* ============================================================
   AM2T — Cart Progress
   <cart-progress> renders inside the cart drawer top.
   - Pack notice: shows missing pack items (top + bottom + socks)
   - Pack confirm: shows when pack is complete (FREE GIFT card)
   - Shipping bar: progress to free shipping threshold
   Listens to 'cart:update' and self-updates.
   ============================================================ */

(() => {
  'use strict';

  if (customElements.get('cart-progress')) return;

  const formatMoney = (cents, currency = 'EUR') => {
    if (typeof cents !== 'number') return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0
      }).format(cents / 100);
    } catch {
      return `${Math.round(cents / 100)} ${currency}`;
    }
  };

  class CartProgress extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.dataset.freeShippingThreshold, 10) || 12000;

      this.packNotice = this.querySelector('[data-pack-notice]');
      this.packConfirm = this.querySelector('[data-pack-confirm]');
      this.packEyebrow = this.querySelector('[data-pack-eyebrow]');
      this.packMsg = this.querySelector('[data-pack-msg]');
      this.greyNotice = this.querySelector('[data-grey-notice]');
      this.greyConfirm = this.querySelector('[data-grey-confirm]');
      this.greyEyebrow = this.querySelector('[data-grey-eyebrow]');
      this.greyMsg = this.querySelector('[data-grey-msg]');
      this.shippingMsg = this.querySelector('[data-shipping-msg]');
      this.shippingFill = this.querySelector('[data-shipping-fill]');

      this.onCartUpdate = this.onCartUpdate.bind(this);
      document.addEventListener('cart:update', this.onCartUpdate);

      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:update', this.onCartUpdate);
    }

    onCartUpdate(e) {
      const cart = e.detail?.cart;
      if (cart) {
        this.render(cart);
      } else {
        this.update();
      }
    }

    async update() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        this.render(await res.json());
      } catch (err) {
        console.warn('[cart-progress] update failed:', err);
      }
    }

    render(cart) {
      const ids = (window.AM2T && window.AM2T.packEligibleIds) || null;
      const items = cart.items || [];

      // Pre-compute pack state (shared by grey block + pack block below).
      let hasTop = false, hasBottom = false, hasSocks = false;
      let packComplete = false, hasAnyPackItem = false;
      if (ids) {
        hasTop = items.some((i) => ids.tops.includes(i.variant_id));
        hasBottom = items.some((i) => ids.bottoms.includes(i.variant_id));
        hasSocks = items.some((i) => ids.socks.includes(i.variant_id));
        packComplete = hasTop && hasBottom && hasSocks;
        hasAnyPackItem = hasTop || hasBottom || hasSocks;
      }

      // === GREY BOTTLE BLOCK — rendered ABOVE pack block (visual order).
      // Hidden completely when pack completes (orange takes over).
      if (this.greyNotice && this.greyConfirm) {
        if (!ids || !ids.greyBottleEnabled || !ids.greyBottleVariantId) {
          this.greyNotice.hidden = true;
          this.greyConfirm.hidden = true;
        } else {
          const subtotal = cart.items_subtotal_price ?? cart.total_price ?? 0;
          const thresholdCents = (ids.greyBottleThreshold || 90) * 100;
          const greyInCart = items.some((i) => i.variant_id === ids.greyBottleVariantId);

          if (packComplete) {
            this.greyNotice.hidden = true;
            this.greyConfirm.hidden = true;
          } else if (greyInCart) {
            this.greyNotice.hidden = true;
            this.greyConfirm.hidden = false;
          } else {
            const remaining = Math.max(0, thresholdCents - subtotal);
            if (this.greyEyebrow) {
              this.greyEyebrow.textContent = `ADD ${formatMoney(remaining, cart.currency)} MORE`;
            }
            if (this.greyMsg) {
              this.greyMsg.textContent = 'to unlock free Triumph Bottle Grey';
            }
            this.greyNotice.hidden = false;
            this.greyConfirm.hidden = true;
          }
        }
      }

      // === PACK STATE — explicit 3-case logic (complete / partial / empty) ===
      if (ids) {
        const orangeId = ids.orangeBottleVariantId;
        const hasBottle = !!(orangeId && items.some((i) => i.variant_id === orangeId));

        if (packComplete) {
          // CASE A: pack complete (regardless of bottle state — engine will add it)
          if (this.packNotice) this.packNotice.hidden = true;
          if (this.packConfirm) this.packConfirm.hidden = false;
        } else if (hasAnyPackItem) {
          // CASE B: partial pack
          if (this.packNotice) this.packNotice.hidden = false;
          if (this.packConfirm) this.packConfirm.hidden = true;

          if (this.packEyebrow && this.packMsg) {
            const missing = [];
            if (!hasTop) missing.push('A TOP');
            if (!hasBottom) missing.push('TIGHTS');
            if (!hasSocks) missing.push('SOCKS');
            this.packEyebrow.textContent = `ADD ${missing.join(' + ')}`;
            this.packMsg.textContent = 'to unlock free Triumph Bottle Orange';
          }
        } else {
          // CASE C: cart empty or no pack items
          if (this.packNotice) this.packNotice.hidden = true;
          if (this.packConfirm) this.packConfirm.hidden = true;
        }
      } else {
        if (this.packNotice) this.packNotice.hidden = true;
        if (this.packConfirm) this.packConfirm.hidden = true;
      }

      // === SHIPPING BAR ===
      if (this.shippingMsg && this.shippingFill) {
        const subtotal = cart.items_subtotal_price ?? cart.total_price ?? 0;
        const remaining = Math.max(0, this.threshold - subtotal);
        const pct = this.threshold > 0
          ? Math.min(100, (subtotal / this.threshold) * 100)
          : 0;

        if (remaining > 0) {
          this.shippingMsg.textContent = `ADD ${formatMoney(remaining, cart.currency)} FOR FREE SHIPPING`;
          this.shippingMsg.classList.remove('is-unlocked');
        } else {
          this.shippingMsg.textContent = '✓ FREE SHIPPING UNLOCKED';
          this.shippingMsg.classList.add('is-unlocked');
        }
        this.shippingFill.style.width = `${pct}%`;
      }
    }
  }

  customElements.define('cart-progress', CartProgress);
})();
